// /frontend/src/services/proposalService.js
// Service untuk menangani proposal generation dan timeline

export class ProposalService {
  constructor() {
    this.companyInfo = {
      name: "Henderson Design Group",
      tagline: "Design & Manufacturing Solutions",
      address: "123 Design Street, Suite 100, New York, NY 10001",
      phone: "+1 (555) 123-4567",
      email: "quotes@hendersondesign.com",
      website: "www.hendersondesign.com"
    };

    this.projectPhases = {
      planning: {
        color: '#3b82f6',
        bgColor: '#eff6ff',
        borderColor: '#dbeafe',
        textColor: '#1e40af',
        description: 'Comprehensive requirement analysis, technical specifications, and detailed design development.'
      },
      payment: {
        color: '#10b981',
        bgColor: '#f0fdf4',
        borderColor: '#dcfce7',
        textColor: '#166534',
        description: 'Payment processing, order confirmation, and financial documentation.'
      },
      design: {
        color: '#8b5cf6',
        bgColor: '#faf5ff',
        borderColor: '#f3e8ff',
        textColor: '#7c3aed',
        description: 'Detailed design work, 3D modeling, and technical drawings preparation.'
      },
      production: {
        color: '#f59e0b',
        bgColor: '#fffbeb',
        borderColor: '#fde68a',
        textColor: '#d97706',
        description: 'High-quality manufacturing using premium materials and advanced production techniques.'
      },
      delivery: {
        color: '#6366f1',
        bgColor: '#eef2ff',
        borderColor: '#c7d2fe',
        textColor: '#4f46e5',
        description: 'Logistics coordination, shipping arrangement, and delivery tracking.'
      },
      installation: {
        color: '#ef4444',
        bgColor: '#fef2f2',
        borderColor: '#fecaca',
        textColor: '#dc2626',
        description: 'Professional on-site installation, configuration, and system testing.'
      },
      completion: {
        color: '#14b8a6',
        bgColor: '#f0fdfa',
        borderColor: '#ccfbf1',
        textColor: '#0f766e',
        description: 'Final walkthrough, documentation handover, and project sign-off.'
      },
      support: {
        color: '#64748b',
        bgColor: '#f8fafc',
        borderColor: '#e2e8f0',
        textColor: '#475569',
        description: 'Ongoing support, maintenance guidance, and warranty services.'
      }
    };
  }

  // Calculate business days
  addBusinessDays(startDate, days) {
    const result = new Date(startDate);
    let addedDays = 0;
    
    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++;
      }
    }
    
    return result;
  }

  // Generate comprehensive timeline
  generateProjectTimeline(customDurations = {}) {
    const today = new Date();
    
    const defaultDurations = {
      quoteReview: 2,
      clientApproval: 3,
      paymentProcessing: 2,
      designPhase: 8,
      productionPhase: 15,
      qualityAssurance: 3,
      delivery: 4,
      installation: 2,
      handover: 1,
      ...customDurations
    };

    let currentDate = new Date(today);
    
    return {
      quoteSubmission: {
        title: "Quote Submission",
        description: "Initial quote request submitted by client",
        date: new Date(today),
        duration: "Day 0",
        status: "completed",
        phase: "planning",
        deliverables: ["Requirements document", "Initial assessment"],
        responsible: "Client"
      },
      
      quoteReview: {
        title: "Quote Review & Technical Analysis",
        description: "Our engineering team reviews requirements and conducts feasibility analysis",
        date: currentDate = this.addBusinessDays(currentDate, 0),
        duration: `${defaultDurations.quoteReview} Business Days`,
        status: "pending",
        phase: "planning",
        deliverables: ["Technical assessment", "Detailed quotation", "Project timeline"],
        responsible: "Henderson Design Team"
      },
      
      clientApproval: {
        title: "Client Review & Approval",
        description: "Client reviews quotation, terms, and provides project approval",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.quoteReview),
        duration: `${defaultDurations.clientApproval} Business Days`,
        status: "upcoming",
        phase: "planning",
        deliverables: ["Signed contract", "Project approval", "Initial payment"],
        responsible: "Client"
      },
      
      paymentProcessing: {
        title: "Payment Processing & Project Setup",
        description: "Process initial payment and establish project infrastructure",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.clientApproval),
        duration: `${defaultDurations.paymentProcessing} Business Days`,
        status: "upcoming",
        phase: "payment",
        deliverables: ["Payment confirmation", "Project folder setup", "Team assignment"],
        responsible: "Henderson Finance & Project Team"
      },
      
      designPhase: {
        title: "Design & Engineering Phase",
        description: "Detailed design development, 3D modeling, and technical specifications",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.paymentProcessing),
        duration: `${defaultDurations.designPhase} Business Days`,
        status: "upcoming",
        phase: "design",
        deliverables: ["3D models", "Technical drawings", "Material specifications", "Design approval"],
        responsible: "Design & Engineering Team"
      },
      
      productionPhase: {
        title: "Manufacturing & Production",
        description: "Material procurement, manufacturing, and component assembly",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.designPhase),
        duration: `${defaultDurations.productionPhase} Business Days`,
        status: "upcoming",
        phase: "production",
        deliverables: ["Manufactured components", "Assembly completion", "Initial testing"],
        responsible: "Production Team"
      },
      
      qualityAssurance: {
        title: "Quality Control & Testing",
        description: "Comprehensive quality checks, performance testing, and final inspection",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.productionPhase),
        duration: `${defaultDurations.qualityAssurance} Business Days`,
        status: "upcoming",
        phase: "production",
        deliverables: ["Quality report", "Test results", "Packaging completion"],
        responsible: "Quality Assurance Team"
      },
      
      delivery: {
        title: "Shipping & Logistics",
        description: "Packaging, shipping coordination, and delivery to client location",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.qualityAssurance),
        duration: `${defaultDurations.delivery} Business Days`,
        status: "upcoming",
        phase: "delivery",
        deliverables: ["Shipped products", "Tracking information", "Delivery confirmation"],
        responsible: "Logistics Team"
      },
      
      installation: {
        title: "Installation & Setup",
        description: "On-site installation, system configuration, and initial testing",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.delivery),
        duration: `${defaultDurations.installation} Business Days`,
        status: "upcoming",
        phase: "installation",
        deliverables: ["Installed system", "Configuration complete", "Initial training"],
        responsible: "Installation Team"
      },
      
      handover: {
        title: "Project Handover & Documentation",
        description: "Final walkthrough, documentation transfer, and project completion",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.installation),
        duration: `${defaultDurations.handover} Business Day`,
        status: "upcoming",
        phase: "completion",
        deliverables: ["User manuals", "Warranty documentation", "Training materials"],
        responsible: "Project Manager"
      },
      
      warranty: {
        title: "Warranty Period & Support",
        description: "12-month comprehensive warranty with dedicated support",
        date: currentDate = this.addBusinessDays(currentDate, defaultDurations.handover),
        duration: "12 Months",
        status: "upcoming",
        phase: "support",
        deliverables: ["Support hotline", "Maintenance schedule", "Warranty coverage"],
        responsible: "Customer Support"
      }
    };
  }

  // Calculate total project duration
  calculateProjectDuration(timeline) {
    const startDate = timeline.quoteSubmission.date;
    const endDate = timeline.warranty.date;
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  // Get phase statistics
  getPhaseStatistics(timeline) {
    const phases = {};
    
    Object.values(timeline).forEach(milestone => {
      if (!phases[milestone.phase]) {
        phases[milestone.phase] = {
          count: 0,
          totalDays: 0,
          milestones: []
        };
      }
      phases[milestone.phase].count++;
      phases[milestone.phase].milestones.push(milestone);
    });

    return phases;
  }

  // Generate risk assessment
  generateRiskAssessment() {
    return [
      {
        category: "Technical",
        risk: "Design complexity may require additional iterations",
        mitigation: "Regular design reviews and client feedback sessions",
        probability: "Medium",
        impact: "Low"
      },
      {
        category: "Supply Chain",
        risk: "Material availability may affect production timeline",
        mitigation: "Early material procurement and backup supplier identification",
        probability: "Low",
        impact: "Medium"
      },
      {
        category: "Installation",
        risk: "Site conditions may require additional preparation",
        mitigation: "Pre-installation site survey and contingency planning",
        probability: "Medium",
        impact: "Low"
      },
      {
        category: "External",
        risk: "Weather or logistics delays during delivery",
        mitigation: "Flexible scheduling and alternative delivery options",
        probability: "Low",
        impact: "Low"
      }
    ];
  }

  // Generate project scope
  generateProjectScope(cart) {
    const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    const uniqueProducts = cart.items.length;
    
    return {
      overview: "Design and manufacturing project with comprehensive delivery and installation services.",
      objectives: [
        "Deliver high-quality custom manufactured products",
        "Meet all technical specifications and requirements",
        "Ensure timely delivery within agreed timeline",
        "Provide comprehensive installation and setup",
        "Deliver complete documentation and training"
      ],
      deliverables: [
        `${totalItems} custom manufactured items across ${uniqueProducts} product categories`,
        "Complete technical documentation package",
        "Installation and configuration services",
        "User training and operational guidance",
        "12-month warranty and support coverage"
      ],
      exclusions: [
        "Site preparation beyond standard requirements",
        "Additional customization requests after design approval",
        "Third-party software licenses or subscriptions",
        "Extended warranty beyond 12 months"
      ],
      assumptions: [
        "Client will provide accurate requirements and specifications",
        "Site will be accessible for delivery and installation",
        "Standard business hours for installation and setup",
        "Client approval will be provided within specified timeframes"
      ]
    };
  }

  // Format currency (placeholder for future pricing)
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  // Generate proposal metadata
  generateProposalMetadata(quoteRequestId, userInfo) {
    return {
      proposalId: `PROP-${quoteRequestId}-${Date.now()}`,
      version: "1.0",
      generatedDate: new Date(),
      validUntil: this.addBusinessDays(new Date(), 30),
      clientInfo: userInfo,
      projectManager: "Henderson Design Team",
      estimatedValue: "To be determined based on specifications",
      confidentiality: "This proposal contains confidential and proprietary information"
    };
  }
}

// Export singleton instance
export const proposalService = new ProposalService();

// Package.json dependencies yang perlu ditambahkan:
/*
{
  "dependencies": {
    "jspdf": "^2.5.1",
    "html2canvas": "^1.4.1",
    "react-to-print": "^2.14.7"
  }
}
*/

// Installation command:
// npm install jspdf html2canvas react-to-print